/** @jsx jsx */
import { Badge, jsx, Spinner, Text } from "theme-ui";
import PropTypes from "prop-types";
import {
  abhyasiIdRegex,
  validateAbhyasiId,
  validateEmail,
  validatePhoneNumberMobile,
} from "sites-common/utils/validations";
import { useCallback, useState } from "react";
import { has } from "sites-common/utils/lodash";
import { DefaultButton, Stack, StackItem } from "office-ui-fabric-react";
import { colorShades } from "sites-common/utils/fabricStyles";
import { useFetchSrcmApi } from "../../fetch-srcm-api";
import StreamingInput from "../StreamingInput";
import AbhyasiSearchEMIBase from "./SearchAbhyasiEMI/base";
import AbhyasiSearchEMIAdvanced from "./SearchAbhyasiEMI/advanced";

const EMISearchBox = ({
  filters,
  advanced,
  onlyOne,
  label,
  onNewValue,
  fieldProps,
  isPreceptorSearch,
}) => {
  const { fetchSrcmApi } = useFetchSrcmApi();
  const [loading, setLoading] = useState({});

  // const clearEntry = useCallback((key) => setLoading(p => Object.fromEntries(Object.entries(p).filter(([k]) => k!== key) )), [setLoading])

  const fetchAndStoreAdvanced = useCallback(
    (value, skey) => {
      let key = null;
      let val = null;
      if (value) {
        if (skey === "abhyasiid" && validateAbhyasiId(value.toUpperCase())) {
          [key, val] = ["ref", value.toUpperCase()];
        } else if (
          (skey === "mobile" || skey === "phone") &&
          validatePhoneNumberMobile(value)
        ) {
          [key, val] = [
            "mobile",
            value.substring(0, 1) !== "+" && value.length === 10
              ? `+91${value}`
              : value,
          ];
        } else if (skey === "email" && validateEmail(value.toLowerCase())) {
          [key, val] = ["email", value.toLowerCase()];
        } else if (skey === "name") {
          [key, val] = ["name", value];
        } else {
          setLoading((p) => ({
            ...p,
            value: { error: `Invalid Entry: ${value}` },
          }));
          return;
        }
      }

      setLoading((prevState) => {
        if (val in prevState) {
          return prevState;
        }
        const methodParams = {
          ...filters,
          [key]: val,
        };
        if (isPreceptorSearch) {
          methodParams.fields = "id,abhyasi_id,name,gender,city_id";
        }
        fetchSrcmApi({
          api: isPreceptorSearch
            ? `/api/v2/preceptors/`
            : `/api/v2/abhyasis/search/`,
          client: "preceptorCandidateAppClient",
          methodParams,
        })
          .then((res) => {
            if (has(res, ["results", 0, "id"])) {
              setLoading((p) => ({ ...p, [val]: { id: res.results[0].id } }));
              res.results.forEach((res1) => onNewValue(res1, val));
            } else {
              setLoading((p) => ({
                ...p,
                [val]: { error: res.detail || `${val}: Not Found` },
              }));
            }
          })
          .catch((error) => {
            setLoading((p) => ({ ...p, [val]: { error: error.toString() } }));
          });
        return { ...prevState, [val]: { loading: val } };
      });
    },
    [isPreceptorSearch, fetchSrcmApi, setLoading, onNewValue, filters]
  );

  const fetchAndStore = useCallback(
    (value) => {
      if (value) {
        if (validateAbhyasiId(value.toUpperCase())) {
          fetchAndStoreAdvanced(value, "abhyasiid");
        } else if (validatePhoneNumberMobile(value)) {
          fetchAndStoreAdvanced(value, "mobile");
        } else if (validateEmail(value.toLowerCase())) {
          fetchAndStoreAdvanced(value, "email");
        } else {
          setLoading((p) => ({
            ...p,
            value: { error: `Invalid Entry: ${value}` },
          }));
        }
      }
    },
    [fetchAndStoreAdvanced, setLoading]
  );

  const clearErrors = useCallback(
    () =>
      setLoading((p) =>
        Object.fromEntries(Object.entries(p).filter(([, v]) => !("error" in v)))
      ),
    [setLoading]
  );

  const errors = Object.values(loading).filter((v) => "error" in v);
  const loadings = Object.values(loading).filter((v) => "loading" in v);

  return (
    <div>
      <Text variant="sectionTitle">{label}</Text>
      {advanced && (
        <AbhyasiSearchEMIAdvanced onNewValue={fetchAndStoreAdvanced} />
      )}
      {!advanced && onlyOne && (
        <AbhyasiSearchEMIBase onNewValue={fetchAndStore} />
      )}
      {!advanced && !onlyOne && (
        <StreamingInput
          fixed_patterns={[abhyasiIdRegex, "[1-9][0-9]{9}"]}
          sep_patterns={["[^\\s]+@[^\\s]+\\.[^\\s]+", "^[+][0-9]+"]}
          sep={[" ", ",", ";"]}
          fieldProps={{
            autoFocus: true,
            multiline: true,
            rows: 5,
            ...fieldProps,
          }}
          onNewValue={fetchAndStore}
        />
      )}
      {errors.length > 0 && (
        <div sx={{ p: 2, my: 2, backgroundColor: colorShades.redShade6 }}>
          <Badge>Errors</Badge>{" "}
          <DefaultButton
            iconProps={{ iconName: "Trash" }}
            onClick={clearErrors}
            text="clear errors"
          />
          <Stack sx={{ my: 2 }} horizontal tokens={{ childrenGap: 5 }} wrap>
            {Object.values(errors).map((v) => (
              <StackItem key={v.id} sx={{ p: 2, border: "solid 1px grey" }}>
                <Text variant="error"> {v.error}</Text>
              </StackItem>
            ))}
          </Stack>
        </div>
      )}
      {loadings.length > 0 && (
        <Stack
          sx={{ my: 2, p: 2, backgroundColor: "yellow" }}
          horizontal
          tokens={{ childrenGap: 5 }}
          wrap
        >
          {loadings.map((v) => (
            <StackItem sx={{ p: 2, border: "solid 1px grey" }}>
              {v.loading} <Spinner size={16} sx={{ mx: 3 }} />
            </StackItem>
          ))}
        </Stack>
      )}
    </div>
  );
};

EMISearchBox.propTypes = {
  onNewValue: PropTypes.func.isRequired,
  fieldProps: PropTypes.shape({}),
  label: PropTypes.string,
  advanced: PropTypes.bool,
  onlyOne: PropTypes.bool,
  filters: PropTypes.shape({}),
  isPreceptorSearch: PropTypes.bool,
};
EMISearchBox.defaultProps = {
  fieldProps: {},
  label: "",
  advanced: false,
  onlyOne: false,
  filters: {},
  isPreceptorSearch: false,
};

export default EMISearchBox;
